import { useEffect, useState, useCallback } from 'react';
import { API_GET, useConfig } from '../api/api';
import Cookies from 'universal-cookie';
import { Box, useTheme, Typography } from '@mui/material';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';
import { tokens } from '../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { delAllCookies } from './Logout';
import StandaloneLogin from './StandaloneLogin';

/**
 * StandaloneAuth is a wrapper component for standalone apps (like chat-app, email-app)
 * that handles authentication independently without redirecting to the main login page.
 *
 * When not authenticated, it shows an embedded login form.
 * On successful login, it renders the children (the actual app content).
 * On logout, it shows the login form again without redirecting.
 */
const StandaloneAuth = ({ children, appName = 'App', appLogo }) => {
  const cookies = new Cookies();
  const [authorized, setAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [authKey, setAuthKey] = useState(0); // Key to force remount children after re-auth
  const lng = cookies.get('lng');
  const config = useConfig();

  const checkAuth = useCallback(() => {
    if (!config) return;

    setIsChecking(true);
    fetch(config.API_AUTH, API_GET).then((res) => {
      if (!res.ok) {
        setIsChecking(false);
        setAuthorized(false);
        if (cookies.get('accessToken')) {
          delAllCookies();
        }
      } else {
        setAuthorized(true);
        setIsChecking(false);
      }
    }).catch(() => {
      setIsChecking(false);
      setAuthorized(false);
    });
  }, [config]);

  useEffect(() => {
    // Set language from cookie
    if (lng) i18n.changeLanguage(lng);
    checkAuth();
  }, [config, lng, checkAuth]);

  const handleLoginSuccess = () => {
    setAuthKey(prev => prev + 1); // Increment key to force remount children
    setAuthorized(true);
    // Dispatch event for providers to refetch data
    window.dispatchEvent(new CustomEvent('standaloneAuthChange', { detail: { authenticated: true } }));
  };

  const handleLogout = useCallback(() => {
    delAllCookies();
    setAuthorized(false);
  }, []);

  // Expose logout function via window for standalone apps to use
  useEffect(() => {
    window.standaloneLogout = handleLogout;
    return () => {
      delete window.standaloneLogout;
    };
  }, [handleLogout]);

  if (isChecking) {
    return <AuthCheckingScreen />;
  }

  if (!authorized) {
    return <StandaloneLogin onLoginSuccess={handleLoginSuccess} appName={appName} appLogo={appLogo} />;
  }

  // Use key to force remount children after re-authentication
  // This ensures all data is reloaded fresh
  return <Box key={authKey} sx={{ height: '100%', width: '100%' }}>{children}</Box>;
};

const AuthCheckingScreen = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  return (
    <Box
      className="w-full h-full absolute top-0 left-0 z-50 flex justify-center items-center flex-col"
      sx={{
        backgroundColor: colors.glassSolid,
        '& svg': {
          width: '100%',
          height: '100%',
        },
      }}
    >
      <div className="w-40 px-2 py-4 relative">
        <Box
          id="scanner"
          className="w-full top-0 left-0 rounded-full"
          sx={{
            height: '3px',
            backgroundColor: colors.greenAccent[500],
            boxShadow: '0 0 20px 2px ' + colors.greenAccent[500],
          }}
        ></Box>
        <FingerprintOutlinedIcon />
      </div>
      <Typography variant="h2">{t('Authenticate')}</Typography>
    </Box>
  );
};

export default StandaloneAuth;
